"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTooltip = void 0;
const react_1 = __importStar(require("react"));
const Popover_1 = require("../Popover");
const TooltipPopover_1 = require("./components/TooltipPopover/TooltipPopover");
const TooltipContext_1 = require("./context/TooltipContext");
const addEventListener_1 = require("./helpers/addEventListener");
const DEFAULT_CURSOR_RECT = {
    width: 12,
    height: 20
};
const useTooltip = ({ text, title, iconSrc, icon, renderContent, maxWidth, isActive = true, render, delayTimeout = 300, tooltipOffset, style, ['data-selector-id']: initialDataSelectorId }) => {
    const mouseMoveRemoveListenerRef = (0, react_1.useRef)(null);
    const mouseDownRemoveListenerRef = (0, react_1.useRef)(null);
    const showTimeoutRef = (0, react_1.useRef)(null);
    const mousePositionRef = (0, react_1.useRef)(null);
    const shownPositionRef = (0, react_1.useRef)(null);
    const controlRef = (0, react_1.useRef)(null);
    const shownStateRef = (0, react_1.useRef)(false);
    const [isShown, setShownStatus] = (0, react_1.useState)(false);
    const tooltipContext = (0, react_1.useContext)(TooltipContext_1.TooltipContext);
    const [hideTooltip] = (0, react_1.useState)({
        current: () => {
            var _a, _b;
            if (((_a = tooltipContext.current) === null || _a === void 0 ? void 0 : _a.hide) === hideTooltip.current) {
                tooltipContext.current = null;
            }
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
            (_b = mouseMoveRemoveListenerRef.current) === null || _b === void 0 ? void 0 : _b.call(mouseMoveRemoveListenerRef);
            if (shownStateRef.current) {
                setShownStatus(false);
            }
            shownStateRef.current = false;
            shownPositionRef.current = null;
        }
    });
    const showTooltip = (0, react_1.useCallback)(() => {
        var _a;
        (_a = tooltipContext.current) === null || _a === void 0 ? void 0 : _a.hide();
        tooltipContext.current = { hide: hideTooltip.current };
        setShownStatus(true);
        shownStateRef.current = true;
    }, [hideTooltip, tooltipContext]);
    (0, react_1.useEffect)(() => {
        const mouseMoveRemoveListener = mouseMoveRemoveListenerRef.current;
        const mouseDownRemoveListener = mouseDownRemoveListenerRef.current;
        const documentMouseDownHandler = () => {
            hideTooltip.current();
        };
        document.addEventListener('mousedown', documentMouseDownHandler, { capture: true });
        return () => {
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
            mouseMoveRemoveListener === null || mouseMoveRemoveListener === void 0 ? void 0 : mouseMoveRemoveListener();
            mouseDownRemoveListener === null || mouseDownRemoveListener === void 0 ? void 0 : mouseDownRemoveListener();
            document.removeEventListener('mousedown', documentMouseDownHandler, { capture: true });
        };
    }, [hideTooltip]);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (!isActive) {
            (_a = mouseMoveRemoveListenerRef.current) === null || _a === void 0 ? void 0 : _a.call(mouseMoveRemoveListenerRef);
            (_b = mouseDownRemoveListenerRef.current) === null || _b === void 0 ? void 0 : _b.call(mouseDownRemoveListenerRef);
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
            shownPositionRef.current = null;
            if (shownStateRef.current) {
                hideTooltip.current();
            }
        }
    }, [hideTooltip, isActive]);
    const popover = (0, Popover_1.usePopover)({
        config: [
            { xAxis: Popover_1.X_AXIS.outsideRight, yAxis: Popover_1.Y_AXIS.outsideBottom, offsetY: 0 },
            { xAxis: Popover_1.X_AXIS.middle, yAxis: Popover_1.Y_AXIS.outsideBottom, offsetY: 0 },
            { xAxis: Popover_1.X_AXIS.insideRight, yAxis: Popover_1.Y_AXIS.outsideBottom, offsetY: 0 },
            { xAxis: Popover_1.X_AXIS.outsideRight, yAxis: Popover_1.Y_AXIS.outsideTop, offsetY: -6 },
            { xAxis: Popover_1.X_AXIS.middle, yAxis: Popover_1.Y_AXIS.outsideTop, offsetY: -6 },
            { xAxis: Popover_1.X_AXIS.insideRight, yAxis: Popover_1.Y_AXIS.outsideTop, offsetY: -6 }
        ],
        target: {
            measure: () => {
                if (!shownPositionRef.current) {
                    return null;
                }
                const { x, y } = shownPositionRef.current;
                const cursor = tooltipOffset || DEFAULT_CURSOR_RECT;
                return Object.assign({ top: y, left: x }, cursor);
            }
        },
        render: (ref) => (react_1.default.createElement(TooltipPopover_1.TooltipPopover, { ref: ref, style: style, "data-selector-id": initialDataSelectorId || 'tooltip', text: text, title: title, iconSrc: iconSrc, icon: icon, renderContent: renderContent, maxWidth: maxWidth }))
    });
    const onMouseEnter = (0, react_1.useCallback)(() => {
        if (!isActive) {
            return;
        }
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
        }
        const timeoutCallback = () => {
            var _a;
            showTimeoutRef.current = null;
            (_a = mouseDownRemoveListenerRef.current) === null || _a === void 0 ? void 0 : _a.call(mouseDownRemoveListenerRef);
            if (mousePositionRef.current) {
                shownPositionRef.current = mousePositionRef.current;
                showTooltip();
            }
        };
        mouseMoveRemoveListenerRef.current = (0, addEventListener_1.addEventListener)(window, 'mousemove', (e) => {
            mousePositionRef.current = { x: e.clientX, y: e.clientY };
        });
        mouseDownRemoveListenerRef.current = (0, addEventListener_1.addEventListener)(window, 'mousedown', () => {
            hideTooltip.current();
        });
        showTimeoutRef.current = window.setTimeout(timeoutCallback, delayTimeout);
    }, [delayTimeout, hideTooltip, isActive, showTooltip]);
    const onMouseLeave = (0, react_1.useCallback)(() => {
        var _a, _b;
        if (!isActive) {
            return;
        }
        (_a = mouseMoveRemoveListenerRef.current) === null || _a === void 0 ? void 0 : _a.call(mouseMoveRemoveListenerRef);
        (_b = mouseDownRemoveListenerRef.current) === null || _b === void 0 ? void 0 : _b.call(mouseDownRemoveListenerRef);
        hideTooltip.current();
    }, [hideTooltip, isActive]);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        render({ ref: controlRef, onMouseEnter, onMouseLeave }),
        isActive && isShown && popover));
};
exports.useTooltip = useTooltip;
//# sourceMappingURL=useTooltip.js.map